import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { Notification, mapNotificationFromApi, NotificationApiResponse } from '../types/notification';

const POLLING_INTERVAL = 60000; // 60 seconds fallback polling

export function useNotificationChannel() {
  const { user, isAuthenticated } = useAuthStore();
  const { fetchUnreadCount, fetchNotifications, addNotification } = useNotificationStore();
  const channelRef = useRef<ReturnType<typeof window.Echo.private> | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    // Initial fetch
    fetchUnreadCount();
    fetchNotifications();

    // Setup WebSocket channel for real-time notifications
    const setupChannel = async () => {
      try {
        // Wait for Echo to be initialized
        const echo = await import('../lib/echo').then(m => m.default);
        
        if (!echo) {
          console.warn('[useNotificationChannel] Echo not available, falling back to polling');
          startPolling();
          return;
        }

        // Subscribe to user's private channel
        const channelName = `user.${user.id}`;
        channelRef.current = echo.private(channelName);

        // Listen for NotificationCreated events
        channelRef.current.listen('.NotificationCreated', (event: NotificationApiResponse) => {
          const notification = mapNotificationFromApi(event);
          addNotification(notification);
        });

        console.log(`[useNotificationChannel] Subscribed to channel: ${channelName}`);
      } catch (error) {
        console.error('[useNotificationChannel] Failed to setup channel:', error);
        startPolling();
      }
    };

    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    };

    setupChannel();

    // Cleanup
    return () => {
      if (channelRef.current) {
        channelRef.current.stopListening('.NotificationCreated');
        // Leave the channel
        if (window.Echo && user?.id) {
          window.Echo.leave(`user.${user.id}`);
        }
        channelRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, fetchUnreadCount, fetchNotifications, addNotification]);
}

export default useNotificationChannel;
